import type { ReactNode } from 'react';
import styled from 'styled-components';

export interface InfoSectionData {
  title: string;
  rows: {
    label: string;
    value: string;
    type: 'text' | 'link' | 'component';
    component?: ReactNode;
  }[];
}

interface InfoSectionProps {
  sections: InfoSectionData[];
}

export function InfoSection({ sections }: InfoSectionProps) {
  return (
    <InfoSectionContainer>
      {sections.map((section, sectionIndex) => (
        <Section key={sectionIndex}>
          <SectionTitle>{section.title}</SectionTitle>
          <InfoGrid>
            {section.rows.map((row, rowIndex) => (
              <Row key={rowIndex}>
                <Label>{row.label}</Label>
                <Value>
                  {row.type === 'component' ? (
                    row.component
                  ) : row.type === 'link' ? (
                    <Link href={row.value}>{row.value}</Link>
                  ) : (
                    row.value
                  )}
                </Value>
              </Row>
            ))}
          </InfoGrid>
        </Section>
      ))}
    </InfoSectionContainer>
  );
}

export const InfoSectionContainer = styled.div`
  background: var(--white);
  border-radius: 8px;
  box-shadow: var(--shadow-md);
  padding: 25px 30px 31px;
`;

const Section = styled.div`
  margin-bottom: 45px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  border-bottom: 1px solid var(--grey-300);
  color: var(--grey-900);
  font-family: var(--raleway);
  font-size: 17px;
  font-weight: 300;
  letter-spacing: 0.5px;
  margin: 0 0 16px 0;
  padding-bottom: 9px;
`;

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10.75px;
`;

const Row = styled.div`
  align-items: start;
  display: grid;
  gap: 16px;
  grid-template-columns: 345px 1fr;

  @media (max-width: 768px) {
    gap: 8px;
    grid-template-columns: 1fr;
  }
`;

const Label = styled.div`
  color: var(--grey-600);
  font-family: var(--raleway);
  font-size: 14px;
  font-weight: 300;
  letter-spacing: 0.1px;
  line-height: 1.8;
`;

const Value = styled.div`
  color: var(--grey-900);
  font-family: var(--raleway);
  font-size: 14px;
  font-weight: 300;
  letter-spacing: 0.1px;
  line-height: 1.8;
`;

const Link = styled.a`
  color: var(--wbs-red);
  text-decoration: none;
`;
